import { Grid, Typography, useEventCallback } from '@mui/material';
import { Cropper } from 'shell/components/shared/cropper';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useFormik, useFormikContext } from 'formik';
import {
  CertificateType,
  DocumentCitizenshipFormVM,
} from 'civil-status/view-models/document-birth-form';
import { CertificationFormVM } from 'civil-status/view-models/document-birth-form';
import { PageMode } from 'civil-status/view-models/page-mode';
import DescriptionForm from 'civil-status/components/shared/description-document';
import { StickyDiv } from 'shell/components/shared/sticky-div';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __CIVIL_STATUS_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import createSchema from '../../../../../constants/create-schema-birth';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import { DocumentCitizenshipCM } from 'shell/models/document-citizenship';
import { transformCertificateTitle } from 'civil-status/utils/transform-certificate-title';
import AnnotationForm from 'civil-status/components/shared/annotation';
import DocumentVisualizationsButtonsForm from './buttons';
import { blobToBase64 } from 'shell/utils/base64-to-blob';
import CitizenshipFormComponent from 'civil-status/components/shared/citizenship-form';
import {
  transformCitizenshipDocument,
  transformCitizenshipRequest,
} from 'civil-status/utils/transform-document-citizenship';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { noop } from 'shell/utils/noop';

interface DocumentVisualizationCitizenshipProps {
  onBack: () => void;
  onNext: (values: DocumentCitizenshipFormVM) => void;
  type: string;
}

const DocumentVisualizationCitizenshipComponent = ({
  onBack,
  onNext,
  type,
}: DocumentVisualizationCitizenshipProps): React.ReactElement => {
  const formContext = useFormikContext<CertificationFormVM>();

  const { values: params } = formContext;

  const [citizenship, getData] = useHttpFetch<
    HttpResponse<DocumentCitizenshipCM>,
    DocumentCitizenshipFormVM
  >(
    `${__CIVIL_STATUS_API_URL__}/atti-cittadinanza-sima/visualizzazione-atto-cittadinanza/${params.chosenDocument?.actNumber}`,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
      },
      transformer: transformCitizenshipDocument,
    }
  );

  const updateBirth = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}/atti-cittadinanza-sima/modifica-atto-cittadinanza`,
    {
      options: {
        method: 'PUT',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const [mode, setMode] = useState<PageMode>(PageMode.Detail);

  const formikForm = useFormik<DocumentCitizenshipFormVM>({
    initialValues: citizenship.data as DocumentCitizenshipFormVM,
    enableReinitialize: true,
    validationSchema: createSchema,
    onSubmit: (values: DocumentCitizenshipFormVM, context) => {
      // If edit mode
      if (mode === PageMode.Edit) {
        const data = transformCitizenshipRequest(values);
        (
          updateBirth({
            data,
          }) as ResponsePromise<void>
        ).then(() => {
          unlockDocumentSubmit();
          toast.success('Operazione eseguita con successo');
          setMode(PageMode.Detail);
          context.setFieldValue('indicatorUpdate', '');
        });

        return;
      }

      unlockDocumentSubmit();
      onNext(values);
    },
  });

  const { values, errors, touched, setFieldValue: onChange } = formikForm;

  const lockDocument = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}/atti-cittadinanza-sima/blocca-atto`,
    {
      options: {
        errorCatching: true,
        method: 'PUT',
        loading: true,
      },
    }
  );

  useEffect(() => {
    window.scrollTo(0, 0);

    // anno - registro - numero atto - parte - serie
    const arrActNumber = (params.chosenDocument?.actNumber || '').split('-');

    (
      lockDocument({
        data: {
          key: {
            anno: arrActNumber[0],
            numeroRegistro: arrActNumber[1],
            numeroAtto: arrActNumber[2],
            parte: arrActNumber[3],
            serie1: arrActNumber[4],
          },
        },
      }) as ResponsePromise<void>
    ).then(() => {
      getData();
    });
  }, []);

  const unlockDocument = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}/atti-cittadinanza-sima/sblocca-atto`,
    {
      options: {
        errorCatching: true,
        method: 'PUT',
        loading: true,
      },
    }
  );

  const unlockDocumentSubmit = useEventCallback(() => {
    unlockDocument({
      data: {
        key: {
          numeroAtto: values.documentNumber,
          numeroRegistro: values.registerNumber,
          parte: values.portion,
          serie1: `${values.serie || ''}${values.serie2 || ''}`,
          anno: values.year,
        },
      },
    });
  });

  const atto = useMemo(() => {
    return transformCertificateTitle({
      type: formContext.values?.chosenDocument?.element?.codiceAtto as CertificateType,
    });
  }, [formContext.values?.chosenDocument?.element?.codiceAtto]);

  const onChanges = useCallback(
    (newValues) => {
      formikForm.setValues({
        ...formikForm.values,
        ...newValues,
      });
    },
    [formikForm.setValues, formikForm.values]
  );

  return (
    <>
      <Typography
        color="secondary.main"
        style={{
          fontSize: '26px',
        }}
      >
        Visualizzazione Atto {atto.type}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <SearchMessages
            type={MessageType.Error}
            title={!formikForm.isValid ? `Sono presenti errori` : ''}
            messages={
              !formikForm.isValid
                ? [
                    {
                      id: 1,
                      value: "Entrare in modifica dell'atto e compilare i campi obbligatori",
                    },
                  ]
                : []
            }
            visibility={!formikForm.isValid}
            onChangeVisibility={noop}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <DocumentVisualizationsButtonsForm
            values={values}
            type={type}
            handleSubmit={() => formikForm.handleSubmit()}
            mode={mode}
            onBack={onBack}
            onChange={onChange}
            setMode={setMode}
          />
          <DescriptionForm values={values} />
          {mode === PageMode.Detail || mode === PageMode.Edit ? (
            <CitizenshipFormComponent
              onChange={onChange}
              onChanges={onChanges}
              values={values}
              errors={errors}
              mode={mode}
              touched={touched}
            />
          ) : undefined}
          <AnnotationForm
            values={values}
            onChange={onChange}
            mode={mode}
            type={type}
            setMode={setMode}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <StickyDiv>
            <Cropper
              images={values?.uploadFile?.raw || []}
              disabled={!values?.uploadFile?.raw?.length}
              onCut={(blob, indexImage) => {
                if (!blob) {
                  return;
                }
                blobToBase64(blob).then((base64) => {
                  onChange('uploadFile', {
                    ...(values?.uploadFile || {}),
                    raw:
                      values?.uploadFile?.raw?.map((el, index) =>
                        index === indexImage ? base64 : el
                      ) || [],
                    rawTiff: base64,
                  });
                });
              }}
            />
          </StickyDiv>
        </Grid>
      </Grid>
    </>
  );
};

DocumentVisualizationCitizenshipComponent.displayName = 'Document Citizenship form component';

const DocumentVisualizationCitizenshipForm = memo(DocumentVisualizationCitizenshipComponent);

export default DocumentVisualizationCitizenshipForm;
